import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Label, Card, HelperText } from '../components/ui';
import { createHousehold } from '../lib/createHousehold';
import { joinViaInviteCode } from '../lib/joinHousehold';
import { useBillingStatus, startCheckout, refreshClaims } from '../lib/billing';
import { Plus, X, Home, Users, ArrowLeft, CreditCard } from 'lucide-react';

// =============================================================================
// WelcomePage — first-run setup (v0.2.42 choice flow; v0.2.43 billing gate)
//
//   • Create a new household — no code. When billing is ON, the founder must
//     subscribe first ($14.99/yr); they're sent to Stripe Checkout and returned
//     to /welcome?sub=active, where the household is created. When billing is
//     OFF (default / dark), creation is immediate and free.
//   • Join an existing household — enter the member invite code (never gated by
//     billing; invited members don't pay).
// =============================================================================

type Mode = 'choose' | 'create' | 'join';

const FORM_KEY = 'hsp_welcome_create';

export function WelcomePage() {
  const { user, membership, refreshMembership, signOut } = useAuth();
  const billing = useBillingStatus();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const prefilledCode = (params.get('code') || '').toUpperCase();
  const checkoutReturn = params.get('sub'); // 'active' | 'cancelled' | null

  const [mode, setMode] = useState<Mode>(
    prefilledCode ? 'join' : checkoutReturn ? 'create' : 'choose'
  );

  const [householdName, setHouseholdName] = useState('');
  const [parentName, setParentName] = useState('');
  const [applicantNames, setApplicantNames] = useState<string[]>(['']);
  const [joinCode, setJoinCode] = useState(prefilledCode);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Bounce existing members to the dashboard.
  useEffect(() => {
    if (membership) nav('/', { replace: true });
  }, [membership, nav]);

  // Default the parent name from the email local-part.
  useEffect(() => {
    if (user?.email && !parentName) {
      const local = user.email.split('@')[0];
      setParentName(local.charAt(0).toUpperCase() + local.slice(1));
    }
  }, [user, parentName]);

  // Returning from Stripe Checkout — restore the form the user had filled in,
  // and refresh the token so the new subscription claim is visible.
  useEffect(() => {
    if (!checkoutReturn) return;
    try {
      const saved = sessionStorage.getItem(FORM_KEY);
      if (saved) {
        const f = JSON.parse(saved);
        if (typeof f.householdName === 'string') setHouseholdName(f.householdName);
        if (typeof f.parentName === 'string') setParentName(f.parentName);
        if (Array.isArray(f.applicantNames) && f.applicantNames.length) setApplicantNames(f.applicantNames);
      }
    } catch {
      /* ignore */
    }
    if (checkoutReturn === 'active') {
      setNotice('Subscription active — finish setting up your household below.');
      refreshClaims().catch(() => {});
    } else if (checkoutReturn === 'cancelled') {
      setErr('Payment was canceled. Your account is saved — subscribe whenever you’re ready.');
    }
  }, [checkoutReturn]);

  function updateApplicant(index: number, value: string) {
    setApplicantNames((prev) => prev.map((n, i) => (i === index ? value : n)));
  }
  function addApplicant() {
    setApplicantNames((prev) => [...prev, '']);
  }
  function removeApplicant(index: number) {
    setApplicantNames((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSignOut() {
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    nav('/signin', { replace: true });
  }

  function validatedCreateInput(): { householdName: string; parentName: string; applicantNames: string[] } | null {
    const cleanApplicants = applicantNames.map((n) => n.trim()).filter(Boolean);
    if (!householdName.trim()) {
      setErr('Please enter a name for your household.');
      return null;
    }
    if (!parentName.trim()) {
      setErr('Please enter your name.');
      return null;
    }
    if (cleanApplicants.length === 0) {
      setErr('Please enter at least one student applying for scholarships.');
      return null;
    }
    return { householdName: householdName.trim(), parentName: parentName.trim(), applicantNames: cleanApplicants };
  }

  // Whether the founder must subscribe before creating (billing on, no sub yet).
  const mustSubscribe = billing.billingEnabled && !billing.hasActiveSub;

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!user) {
      setErr('You must be signed in.');
      return;
    }
    const input = validatedCreateInput();
    if (!input) return;

    setSubmitting(true);
    try {
      if (mustSubscribe) {
        // Save the form, then hand off to Stripe Checkout.
        sessionStorage.setItem(FORM_KEY, JSON.stringify(input));
        await startCheckout(); // navigates away
        return;
      }
      // Subscribed (or billing off). If billing is on, make sure the claim the
      // rules read is fresh before the gated create.
      if (billing.billingEnabled) await refreshClaims();
      await createHousehold({ user, ...input });
      sessionStorage.removeItem(FORM_KEY);
      await refreshMembership();
      nav('/', { replace: true });
    } catch (e) {
      console.error('[Welcome] create/checkout failed:', e);
      const msg = e instanceof Error ? e.message : 'Could not create your household.';
      setErr(
        /permission|insufficient/i.test(msg)
          ? 'Your payment may still be processing. Wait a few seconds and try again.'
          : msg
      );
      setSubmitting(false);
    }
  }

  async function handleJoin(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!user) {
      setErr('You must be signed in.');
      return;
    }
    if (!joinCode.trim()) {
      setErr('Please enter the invite code you were given.');
      return;
    }
    setSubmitting(true);
    try {
      await joinViaInviteCode({
        uid: user.uid,
        code: joinCode.trim(),
        email: user.email ?? '',
        displayName: user.displayName || (user.email ?? '').split('@')[0] || 'New member',
      });
      await refreshMembership();
      nav('/', { replace: true });
    } catch (e) {
      console.error('[Welcome] join failed:', e);
      setErr(e instanceof Error ? e.message : 'Could not join that household.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4 py-8">
      <Card className="w-full max-w-lg p-8">
        {mode === 'choose' && (
          <>
            <h1 className="font-serif text-2xl font-semibold mb-1">Welcome! Let's get you set up</h1>
            <p className="text-sm text-soft mb-6">
              Are you starting fresh, or joining a family member who already has an account?
            </p>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => { setErr(null); setMode('create'); }}
                className="flex w-full items-start gap-3 rounded-lg border border-line p-4 text-left transition hover:border-forest-300 hover:bg-forest-50/40"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-forest-100 text-forest-800">
                  <Home size={18} />
                </span>
                <span>
                  <span className="block font-medium text-ink">Create a new household</span>
                  <span className="block text-sm text-soft">
                    Set up your family's workspace and start tracking scholarships.
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setErr(null); setMode('join'); }}
                className="flex w-full items-start gap-3 rounded-lg border border-line p-4 text-left transition hover:border-forest-300 hover:bg-forest-50/40"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <Users size={18} />
                </span>
                <span>
                  <span className="block font-medium text-ink">Join an existing household</span>
                  <span className="block text-sm text-soft">
                    A parent already set up your family? Enter the invite code they gave you.
                  </span>
                </span>
              </button>
            </div>
          </>
        )}

        {mode === 'create' && (
          <>
            <BackButton onClick={() => { setErr(null); setNotice(null); setMode('choose'); }} disabled={submitting} />
            <h1 className="font-serif text-2xl font-semibold mb-1">Create your household</h1>
            <p className="text-sm text-soft mb-6">
              A household holds your family's scholarship tracking. You can change any
              of this later from the Household page.
            </p>

            {notice && (
              <div className="mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                {notice}
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-5">
              <div>
                <Label htmlFor="hh-name">Household name</Label>
                <Input id="hh-name" value={householdName} onChange={(e) => setHouseholdName(e.target.value)} placeholder="e.g. The Smith Family" autoFocus />
                <HelperText>What you'd like to call your family's space in the app.</HelperText>
              </div>
              <div>
                <Label htmlFor="parent-name">Your name</Label>
                <Input id="parent-name" value={parentName} onChange={(e) => setParentName(e.target.value)} placeholder="Your name" />
                <HelperText>You'll be set up as a parent, able to manage members and applicants.</HelperText>
              </div>
              <div>
                <Label>Students</Label>
                <HelperText>The student(s) applying for scholarships. Add one row per student.</HelperText>
                <div className="space-y-2 mt-2">
                  {applicantNames.map((name, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={name} onChange={(e) => updateApplicant(i, e.target.value)} placeholder={`Student ${i + 1} name`} />
                      {applicantNames.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeApplicant(i)} title="Remove this student">
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={addApplicant} className="mt-2">
                  <Plus size={14} className="mr-1" />Add another student
                </Button>
              </div>

              {mustSubscribe && (
                <div className="flex items-start gap-2 rounded-md border border-forest-200 bg-forest-50 p-3 text-sm text-forest-900">
                  <CreditCard size={16} className="mt-0.5 shrink-0" />
                  <span>
                    HomeScholar Pro is <strong>$14.99/year</strong> for your whole household (up to 8
                    family members). You'll enter payment securely on the next screen, then come back
                    to finish setup.
                  </span>
                </div>
              )}

              {err && (
                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>
              )}

              <Button type="submit" className="w-full" disabled={submitting || billing.loading}>
                {submitting
                  ? (mustSubscribe ? 'Redirecting to payment…' : 'Setting up your household…')
                  : (mustSubscribe ? 'Continue to payment — $14.99/yr' : 'Create my household')}
              </Button>
            </form>
          </>
        )}

        {mode === 'join' && (
          <>
            <BackButton onClick={() => { setErr(null); setMode('choose'); }} disabled={submitting} />
            <h1 className="font-serif text-2xl font-semibold mb-1">Join your household</h1>
            <p className="text-sm text-soft mb-6">
              Enter the invite code a parent in your household shared with you.
            </p>
            <form onSubmit={handleJoin} className="space-y-5">
              <div>
                <Label htmlFor="join-code">Invite code</Label>
                <Input id="join-code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="6-character code" maxLength={6} autoFocus />
                <HelperText>
                  Don't have a code? A parent can generate one from the Household page and share it
                  with you. Invited members don't pay — your household's subscription covers everyone.
                </HelperText>
              </div>

              {err && (
                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Joining…' : 'Join household'}
              </Button>
            </form>
          </>
        )}

        <div className="mt-6 pt-4 border-t border-line text-center">
          <p className="text-sm text-soft">
            {user?.email ? (<>Signed in as <span className="font-medium">{user.email}</span>.</>) : ('Already have an account?')}
          </p>
          <p className="text-sm text-soft mt-1">
            Need a different account?{' '}
            <button type="button" onClick={handleSignOut} className="text-forest-700 hover:underline font-medium" disabled={submitting}>
              Sign out and sign in
            </button>
          </p>
          <p className="text-xs text-soft mt-3">
            Trouble with sign-up or billing?{' '}
            <a href="mailto:homescholarpro@gmail.com" className="text-forest-700 hover:underline font-medium">homescholarpro@gmail.com</a>
          </p>
        </div>
      </Card>
    </div>
  );
}

function BackButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mb-4 inline-flex items-center gap-1 text-sm text-soft hover:text-ink disabled:opacity-50"
    >
      <ArrowLeft size={14} /> Back
    </button>
  );
}
